import {createContext, type PropsWithChildren, use, useEffect, useMemo, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {useSearchResults} from './SearchResultsContext'
import {allPublishersOption, getDefaultPublisher} from '../utilities/model-filter-options'
import {useModelsSearchFilters} from '../hooks/use-models-search-filters'
import type {ModelsSearchFiltersPayload} from '../types'

interface ModelsPublisherContextType {
  /**
   * The name of the selected/active publisher filter for use in the search query or URL parameters.
   * Example: 'Meta', 'OpenAI'.
   */
  publisher: string | null
  /**
   * The display name of the selected/active publisher filter for use in rendering filter menus. Omitted if the
   * display name should be the same as `publisher`.
   * Example: 'Azure OpenAI Service'.
   */
  publisherDisplayName?: string | null
  /**
   * The list of valid publishers to show in filter menus.
   */
  publishers: ModelsSearchFiltersPayload['publishers']
  /**
   * Update the selected/active publisher. Pass a value like 'OpenAI', the value for use in the search query or URL
   * parameters.
   */
  setPublisher: (publisher: string | null) => void
}

const ModelsPublisherContext = createContext<ModelsPublisherContextType | undefined>(undefined)

export function useModelsPublisher() {
  const context = use(ModelsPublisherContext)
  if (!context) throw new Error('useModelsPublisher must be used within a ModelsPublisherProvider')
  return context
}

export function ModelsPublisherProvider({children}: PropsWithChildren) {
  const [searchParams] = useSearchParams()
  const {searchResults} = useSearchResults()
  const {
    data: {publishers},
  } = useModelsSearchFilters()
  const initialPublisher = useMemo(() => {
    if (searchParams.get('type') === 'models') {
      return getDefaultPublisher(
        searchParams,
        searchResults.parsedQuery,
        publishers.map(p => p.name),
      )
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishers])
  const [publisher, setPublisher] = useState<string | null>(initialPublisher)
  const publisherDisplayName = useMemo(() => {
    if (publisher === allPublishersOption || !publisher) return allPublishersOption
    const foundPublisher = publishers.find(p => p.name.toLowerCase() === publisher.toLowerCase())
    return foundPublisher ? foundPublisher.displayName : publisher
  }, [publisher, publishers])
  const value = useMemo(
    () => ({publisher, publisherDisplayName, publishers, setPublisher}) satisfies ModelsPublisherContextType,
    [publisher, publisherDisplayName, publishers, setPublisher],
  )

  // Once the list of valid publishers has loaded, the publisher specified in the query string or other URL parameter
  // may now be considered valid, so we should update the selected/active publisher.
  useEffect(() => {
    if (publisher === allPublishersOption && initialPublisher !== allPublishersOption) {
      setPublisher(initialPublisher)
    }
  }, [initialPublisher, publisher])

  return <ModelsPublisherContext value={value}>{children}</ModelsPublisherContext>
}
